import {
  ChangeEvent,
  CSSProperties,
  useCallback,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useAddEditLibrary } from "@hooks/useAddEditLibrary";
import { resolveUploadAccept } from "@utils/mediaTypeAccept";
import { LearningResourceType, MediaType } from "src/interfaces/Media";
import { MediaPickerItem } from "../types";

interface UseMediaUploadArgs {
  corporateId: string;
  mediaType?: MediaType;
  learningResourceType?: LearningResourceType;
  enabled?: boolean;
  onUploaded: (item: MediaPickerItem) => void;
}

const HIDDEN_INPUT_STYLE: CSSProperties = { display: "none" };

export const useMediaUpload = ({
  corporateId,
  mediaType,
  learningResourceType,
  enabled = true,
  onUploaded,
}: UseMediaUploadArgs) => {
  const { t } = useTranslation("media-library-picker");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleStatus = useCallback(
    (args: { s: StatusEnum; message: string }) => {
      if (args.s === StatusEnum.LOADING) {
        setIsUploading(true);
        setUploadError(null);
      } else if (args.s === StatusEnum.OK) {
        setIsUploading(false);
      } else if (args.s === StatusEnum.KO) {
        setIsUploading(false);
        setUploadError(args.message || t("upload-error"));
      }
    },
    [t],
  );

  const { onSubmit } = useAddEditLibrary(
    corporateId || "1",
    undefined,
    handleStatus,
  );

  const canUpload = enabled && !!mediaType;

  const openFileDialog = () => {
    if (isUploading || !canUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !mediaType) return;

    const name = file.name.replace(/\.[^/.]+$/, "");
    const created = await onSubmit(
      { name, mediaType, learningResourceType },
      { file },
    );
    setIsUploading(false);
    if (!created?.id) {
      setUploadError((prev) => prev ?? t("upload-error"));
      return;
    }
    onUploaded({ id: created.id, label: created.metadata?.name || name });
  };

  const clearUploadError = useCallback(() => setUploadError(null), []);

  const inputProps = {
    ref: fileInputRef,
    type: "file" as const,
    accept: resolveUploadAccept(mediaType, learningResourceType),
    disabled: !canUpload,
    onChange: handleFileSelected,
    style: HIDDEN_INPUT_STYLE,
  };

  return {
    inputProps,
    openFileDialog,
    isUploading,
    uploadError,
    clearUploadError,
  };
};
